import { useState, useEffect } from 'react';
import { USER_TYPES } from '../enums';

const DEFAULT_BEAMER_PRODUCT_ID = 'nygCokNA78165';
const BEAMER_POLLING_INTERVAL_MS = 5000;

export class BeamerCore {
  constructor() {
    this.config = null;
    this.isInitialized = false;
    this.isScriptLoaded = false;
    this.scriptLoadPromise = null;
    this.SCRIPT_URL = 'https://app.getbeamer.com/js/beamer-embed.js';
  }

  async initialize(config) {
    if (this.isInitialized) {
      console.warn('Beamer already initialized');
      return;
    }

    if (!config.product_id) {
      throw new Error('Beamer product_id required');
    }

    try {
      this.config = { ...config };
      this.setGlobalConfig(this.config);
      await this.loadScript();
      this.isInitialized = true;
    } catch (error) {
      console.error('Beamer initialization failed:', error);
      this.config?.onerror?.(error);
      throw error;
    }
  }

  setUser(user) {
    if (!this.isInitialized) {
      console.warn('Beamer not initialized');
      return;
    }

    try {
      if (window.beamer_config) {
        Object.assign(window.beamer_config, {
          user_id: user.id,
          user_email: user.email,
          user_firstname: user.firstname,
          user_lastname: user.lastname,
          user_created_at: user.created_at,
        });
      }
      window.Beamer?.setUser?.(user);
    } catch (error) {
      console.error('Failed to set Beamer user:', error);
    }
  }

  clearUser() {
    if (!this.isInitialized) return;

    try {
      if (window.beamer_config) {
        const userFields = ['user_id', 'user_email', 'user_firstname', 'user_lastname', 'user_created_at'];
        userFields.forEach(field => delete window.beamer_config[field]);
      }
      window.Beamer?.clearUser?.();
    } catch (error) {
      console.error('Failed to clear Beamer user:', error);
    }
  }

  show = () => this.executeBeamerAction('show');
  hide = () => this.executeBeamerAction('hide');
  toggle = () => this.executeBeamerAction('toggle');

  update(newConfig) {
    if (!this.isInitialized) {
      console.warn('Beamer not initialized');
      return;
    }

    try {
      this.config = { ...this.config, ...newConfig };
      this.setGlobalConfig(this.config);
      window.Beamer?.update?.(newConfig);
    } catch (error) {
      console.error('Failed to update Beamer config:', error);
    }
  }

  getUnreadCount() {
    return this.isInitialized ? window.Beamer?.getUnreadCount?.() || 0 : 0;
  }

  destroy() {
    try {
      window.Beamer?.destroy?.();
      delete window.beamer_config;

      const existingScript = document.querySelector(`script[src="${this.SCRIPT_URL}"]`);
      existingScript?.remove();

      this.resetState();
    } catch (error) {
      console.error('Failed to destroy Beamer:', error);
    }
  }

  isReady = () => this.isInitialized && this.isScriptLoaded;
  getConfig = () => (this.config ? { ...this.config } : null);

  executeBeamerAction(action) {
    if (!this.isInitialized) {
      console.warn('Beamer not initialized');
      return;
    }

    try {
      window.Beamer?.[action]?.();
    } catch (error) {
      console.error(`Failed to ${action} Beamer:`, error);
    }
  }

  resetState() {
    this.isInitialized = false;
    this.isScriptLoaded = false;
    this.scriptLoadPromise = null;
    this.config = null;
  }

  setGlobalConfig = config => {
    window.beamer_config = { ...config };
  };

  async loadScript() {
    if (this.scriptLoadPromise) return this.scriptLoadPromise;
    if (this.isScriptLoaded) return Promise.resolve();

    const existingScript = document.querySelector(`script[src="${this.SCRIPT_URL}"]`);
    if (existingScript) {
      this.isScriptLoaded = true;
      return Promise.resolve();
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      const script = this.createScript();

      script.onload = () => {
        this.isScriptLoaded = true;
        resolve();
      };

      script.onerror = error => {
        this.scriptLoadPromise = null;
        reject(new Error(`Beamer script load failed: ${error}`));
      };

      this.appendScript(script, reject);
    });

    return this.scriptLoadPromise;
  }

  createScript() {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = this.SCRIPT_URL;
    script.defer = true;
    return script;
  }

  appendScript(script, reject) {
    const target = document.head || document.body;
    if (target) {
      target.appendChild(script);
    } else {
      reject(new Error('No DOM element found for Beamer script'));
    }
  }
}

export const useBeamer = (config, user = null) => {
  const [beamerCore, setBeamerCore] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!config?.product_id) return;

    let intervalId = null;
    let coreInstance = null;

    const initializeBeamer = async () => {
      try {
        const core = new BeamerCore();
        await core.initialize(config);
        coreInstance = core;
        setBeamerCore(core);
        setIsReady(true);

        if (user) {
          core.setUser(user);
        }

        intervalId = setInterval(() => {
          if (core.isReady()) {
            setUnreadCount(core.getUnreadCount());
          }
        }, BEAMER_POLLING_INTERVAL_MS);
      } catch (error) {
        console.error('Failed to initialize Beamer:', error);
      }
    };

    initializeBeamer();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (coreInstance) {
        coreInstance.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, user]);

  useEffect(() => {
    if (beamerCore?.isReady() && user) {
      beamerCore.setUser(user);
    }
  }, [user, beamerCore]);

  const show = () => beamerCore?.show();
  const hide = () => beamerCore?.hide();
  const toggle = () => beamerCore?.toggle();
  const clearUser = () => {
    beamerCore?.clearUser();
    setUnreadCount(0);
  };

  return {
    isReady,
    unreadCount,
    show,
    hide,
    toggle,
    clearUser,
  };
};

export class BeamerService {
  constructor(config = {}) {
    this.beamerCore = new BeamerCore();
    this.config = config;
  }

  async initializeBeamer(user = null, accountId = null) {
    const isEnabled = this.config.BEAMER_ENABLE !== 'false';
    if (!isEnabled) {
      console.log('Beamer disabled via configuration');
      return;
    }

    const productId = this.config.BEAMER_PRODUCT_ID || DEFAULT_BEAMER_PRODUCT_ID;
    if (!productId) {
      console.warn('Beamer product ID missing');
      return;
    }

    try {
      const beamerConfig = this.buildBeamerConfig(productId, user, accountId);
      await this.beamerCore.initialize(beamerConfig);

      if (user) {
        this.setCurrentUser(user);
      }

      console.log('Beamer initialized with filter:', beamerConfig.filter);
    } catch (error) {
      console.error('Beamer initialization failed:', error);
    }
  }

  buildBeamerConfig(productId, user, accountId) {
    const filters = this.buildUserFilters(user, accountId);

    return {
      product_id: productId,
      selector: this.config.BEAMER_SELECTOR,
      display: this.config.BEAMER_POSITION || 'right',
      button_position: this.config.BEAMER_BUTTON_POSITION || 'bottom-right',
      language: this.config.BEAMER_LANGUAGE || 'EN',
      filter: filters.length > 0 ? filters.join(';') : undefined,
      lazy: this.config.BEAMER_LAZY === 'true',
      alert: this.config.BEAMER_ALERT !== 'false',
      counter: this.config.BEAMER_COUNTER !== 'false',
      delay: Number(this.config.BEAMER_DELAY) || 0,
      onopen: () => console.log('Beamer panel opened'),
      onclose: () => console.log('Beamer panel closed'),
      onerror: error => console.error('Beamer error:', error),
    };
  }

  buildUserFilters(user, accountId) {
    const filters = [];

    filters.push('app_requester');

    if (user?.userType) {
      const userTypeMap = {
        [USER_TYPES.DISPATCHER]: 'role_dispatcher',
        [USER_TYPES.REQUESTER]: 'role_requester',
      };
      filters.push(userTypeMap[user.userType] || 'role_unknown');
    }

    if (accountId) {
      filters.push(`account_${accountId}`);
    }

    if (user?.facilities?.length) {
      const facilityFilters = this.buildFacilityFilters(user.facilities);
      filters.push(...facilityFilters);
    }

    if (this.config.BEAMER_FILTER) {
      filters.push(this.config.BEAMER_FILTER);
    }

    return filters;
  }

  buildFacilityFilters(facilities) {
    const filters = [];

    facilities.forEach(facility => {
      if (facility.requesterAgency) {
        const agency = facility.requesterAgency;

        if (agency.requesterAgencyGroup) {
          const group = agency.requesterAgencyGroup;

          if (group.id) {
            filters.push(`rag_id_${group.id}`);
          }

          if (group.name) {
            filters.push(`rag_name_${group.name.replace(/\s+/g, '_').toLowerCase()}`);
          }
        }
      }
    });

    return filters;
  }

  setCurrentUser(user) {
    if (!user) {
      console.warn('No current user found for Beamer');
      return;
    }

    const beamerUser = {
      id: user.id,
      email: user.email,
      firstname: user.firstName,
      lastname: user.lastName,
      ...(user.createdAt && { created_at: user.createdAt }),
    };

    this.beamerCore.setUser(beamerUser);
  }

  clearUser() {
    this.beamerCore.clearUser();
  }

  show = () => this.beamerCore.show();
  hide = () => this.beamerCore.hide();
  toggle = () => this.beamerCore.toggle();
  getUnreadCount = () => this.beamerCore.getUnreadCount();
  updateConfig = config => this.beamerCore.update(config);
  isReady = () => this.beamerCore.isReady();
  destroy = () => this.beamerCore.destroy();
}
